import { useState } from 'react';
import {
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  AreaChart, Area, RadarChart, Radar, PolarGrid, PolarAngleAxis,
  BarChart, Bar, Cell,
} from 'recharts';
import {
  Building2, CheckCircle2, XCircle, AlertTriangle, TrendingUp, Zap,
  ArrowUpRight, ArrowDownRight, Download, RefreshCw, Search, Award,
} from 'lucide-react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import RankingBadge, { parseRanking } from '../components/shared/RankingBadge';

// ─── TYPES & MOCK DATA ───────────────────────────────────────────────────────

const MOCK_KPIS = [
  { label: 'Đơn vị đã kiểm toán', value: 63,    change: +8,   changeLabel: 'so với kỳ trước', icon: <Building2 />, color: '#6366f1' },
  { label: 'Xếp hạng A (Xuất sắc)', value: 24,   change: +3,   changeLabel: 'tăng',             icon: <Award />,       color: '#10b981' },
  { label: 'Loại B-Tốt',            value: 17,   change: +5,   changeLabel: 'tăng',             icon: <CheckCircle2 />, color: '#0ea5e9' },
  { label: 'Loại B-Đạt',            value: 11,   change: -2,   changeLabel: 'giảm',             icon: <CheckCircle2 />, color: '#6366f1' },
  { label: 'Xếp hạng C (Không đạt)', value: 11, change: -4,   changeLabel: 'giảm',             icon: <XCircle />,     color: '#f43f5e' },
  { label: 'Điểm TB toàn quốc',     value: '72.4', change: +4.2, changeLabel: 'tăng',           icon: <TrendingUp />,  color: '#a855f7' },
  { label: 'Kiến nghị chưa xử lý',  value: 147,  change: -23,  changeLabel: 'giảm',             icon: <AlertTriangle />, color: '#f59e0b' },
  { label: 'KPI tự động thu thập',  value: '7/27', change: 0,  icon: <Zap />, color: '#06b6d4' },
];

// Xếp hạng với B_TOT / B_DAT
const MOCK_RANKINGS = [
  { orgName: 'Bộ Công an',         orgCode: 'BCA',  totalScore: 94.5, ranking: 'A',     TC1: 9.2, TC2: 9.0, TC4: 9.5, TC5: 9.8 },
  { orgName: 'Bộ Tài chính',       orgCode: 'BTC',  totalScore: 91.2, ranking: 'A',     TC1: 8.8, TC2: 9.2, TC4: 9.0, TC5: 9.4 },
  { orgName: 'UBND TP. Hà Nội',    orgCode: 'HNI',  totalScore: 85.1, ranking: 'A',     TC1: 8.5, TC2: 8.4, TC4: 8.8, TC5: 8.6 },
  { orgName: 'Bộ Y tế',            orgCode: 'BYT',  totalScore: 76.3, ranking: 'B_TOT', TC1: 7.5, TC2: 7.8, TC4: 7.9, TC5: 7.2 },
  { orgName: 'Bộ NN&PTNT',         orgCode: 'BNNP', totalScore: 73.8, ranking: 'B_TOT', TC1: 7.2, TC2: 7.0, TC4: 7.6, TC5: 7.4 },
  { orgName: 'UBND tỉnh Hải Phòng', orgCode: 'HP', totalScore: 64.2, ranking: 'B_DAT', TC1: 6.5, TC2: 6.0, TC4: 6.8, TC5: 6.4 },
  { orgName: 'UBND tỉnh Nghệ An',  orgCode: 'NGHA', totalScore: 55.1, ranking: 'C',     TC1: 5.0, TC2: 5.5, TC4: 5.8, TC5: 5.2 },
];

// Xu hướng 4 mức xếp hạng
const MOCK_TREND = [
  { period: 'Q2/2024', avg: 63, A: 10, B_TOT: 15, B_DAT: 20, C: 18 },
  { period: 'Q3/2024', avg: 66, A: 13, B_TOT: 18, B_DAT: 18, C: 14 },
  { period: 'Q4/2024', avg: 70, A: 17, B_TOT: 22, B_DAT: 14, C: 10 },
  { period: 'Q1/2025', avg: 72, A: 24, B_TOT: 17, B_DAT: 11, C: 11 },
];

// Distribution chart data
const MOCK_DISTRIBUTION = [
  { name: 'Loại A', value: 24, color: '#10b981' },
  { name: 'B-Tốt',  value: 17, color: '#0ea5e9' },
  { name: 'B-Đạt',  value: 11, color: '#6366f1' },
  { name: 'Loại C', value: 11, color: '#f43f5e' },
];

// ─── SUB-COMPONENTS ───────────────────────────────────────────────
const KPICard = ({ kpi, index }: { kpi: any; index: number }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay: index * 0.05 }}
    className="glass-card p-6 relative overflow-hidden group shadow-sm hover:shadow-xl dark:shadow-none"
  >
    <div className="absolute -right-4 -top-4 w-24 h-24 bg-indigo-500/5 rounded-full blur-2xl group-hover:bg-indigo-500/10 transition-all" />
    <div className="flex items-start justify-between relative z-10">
      <div>
        <p className="text-[10px] font-light text-slate-600 dark:text-slate-400 tracking-wider mb-2">{kpi.label}</p>
        <p className="text-3xl font-heading font-light text-slate-950 dark:text-white leading-tight">{kpi.value}</p>
      </div>
      <div className="p-3.5 rounded-2xl bg-slate-100 dark:bg-white/5 border border-slate-200 dark:border-white/10 shadow-inner" style={{ color: kpi.color }}>
        {kpi.icon}
      </div>
    </div>
    {kpi.change !== undefined && (
      <div className="mt-5 flex items-center gap-2 relative z-10">
        <div className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-light ${
          kpi.change > 0 ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400' : 'bg-rose-500/10 text-rose-600 dark:text-rose-400'
        }`}>
          {kpi.change > 0 ? <ArrowUpRight size={12}/> : <ArrowDownRight size={12}/>}
          {Math.abs(kpi.change)}
        </div>
        <span className="text-[10px] text-slate-600 dark:text-slate-300 font-light tracking-tighter">{kpi.changeLabel}</span>
      </div>
    )}
  </motion.div>
);

// ─── MAIN DASHBOARD ───────────────────────────────────────────────
export default function DashboardNationalPage() {
  const [isRefreshing] = useState(false);
  const { t } = useTranslation();

  return (
    <div className="space-y-8">
      {/* Header section inside content */}
      <div className="flex items-end justify-between">
        <div>
          <h2 className="text-3xl font-heading text-slate-950 dark:text-white tracking-tight">{t('labels.dashboardOverview')}</h2>
          <p className="text-slate-600 dark:text-slate-400 font-light mt-1">{t('labels.dashboardSubtitle')}</p>
        </div>
        <div className="flex items-center gap-3">
          <button className="btn-outline">
            <RefreshCw size={16} className={isRefreshing ? 'animate-spin' : ''} />
            {t('labels.refresh')}
          </button>
          <button className="btn-futuristic">
            <Download size={16} />
            {t('labels.exportReport')}
          </button>
        </div>
      </div>

      {/* KPI Grid — 8 chỉ số */}
      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-8 gap-4">
        {MOCK_KPIS.map((kpi, i) => <KPICard key={kpi.label} kpi={kpi} index={i} />)}
      </div>

      {/* Main Charts area */}
      <div className="grid grid-cols-12 gap-8">
        {/* Trend Area */}
        <div className="col-span-12 lg:col-span-8 space-y-8">
          <div className="glass-panel p-8 shadow-sm">
            <h3 className="text-lg font-heading text-slate-800 dark:text-white mb-8 flex items-center gap-2">
              <TrendingUp size={20} className="text-primary" />
              Xu hướng điểm số & xếp hạng
            </h3>
            <div className="h-[350px]">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={MOCK_TREND}>
                  <defs>
                    <linearGradient id="colorAvg" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#4F46E5" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#4F46E5" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="currentColor" className="text-slate-200 dark:text-white/5" vertical={false} />
                  <XAxis dataKey="period" axisLine={false} tickLine={false} tick={{fill: '#64748b', fontSize: 10, fontWeight: 300}} dy={10} />
                  <YAxis axisLine={false} tickLine={false} tick={{fill: '#64748b', fontSize: 10, fontWeight: 300}} dx={-10} />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'var(--bg-card)', 
                      border: '1px solid var(--border-glass)', 
                      borderRadius: '16px', 
                      color: 'var(--text-primary)',
                      backdropFilter: 'blur(20px)',
                      boxShadow: '0 20px 25px -5px rgb(0 0 0 / 0.1)'
                    }}
                    itemStyle={{ fontSize: '11px', fontWeight: 300 }}
                  />
                  <Area type="monotone" dataKey="avg" stroke="var(--primary)" strokeWidth={4} fillOpacity={1} fill="url(#colorAvg)" />
                  <Area type="monotone" dataKey="A"     stroke="#10b981" strokeWidth={1.5} fillOpacity={0} />
                  <Area type="monotone" dataKey="B_TOT" stroke="#0ea5e9" strokeWidth={1.5} fillOpacity={0} strokeDasharray="4 2" />
                  <Area type="monotone" dataKey="B_DAT" stroke="#6366f1" strokeWidth={1.5} fillOpacity={0} strokeDasharray="4 2" />
                  <Area type="monotone" dataKey="C"     stroke="#f43f5e" strokeWidth={1.5} fillOpacity={0} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="glass-panel p-8 shadow-sm">
             <div className="flex items-center justify-between mb-8">
                <h3 className="text-lg font-heading text-slate-950 dark:text-white">Bảng xếp hạng đơn vị nổi bật</h3>
                <div className="px-4 py-2.5 bg-slate-100 dark:bg-black/20 border border-slate-200 dark:border-white/5 rounded-2xl flex items-center gap-3 w-64 focus-within:border-primary transition-all shadow-inner">
                   <Search size={16} className="text-slate-600" />
                   <input type="text" placeholder="Tìm kiếm đơn vị..." className="bg-transparent outline-none text-sm text-slate-950 dark:text-white placeholder:text-slate-500 font-light" />
                </div>
             </div>
             <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="text-left border-b border-slate-100 dark:border-white/5">
                      <th className="pb-5 text-[10px] font-light text-slate-500 dark:text-slate-400 tracking-widest pl-4">Đơn vị</th>
                      <th className="pb-5 text-[10px] font-light text-slate-500 dark:text-slate-400 tracking-widest">Loại</th>
                      <th className="pb-5 text-[10px] font-light text-slate-500 dark:text-slate-400 tracking-widest text-center">Xếp hạng</th>
                      <th className="pb-5 text-[10px] font-light text-slate-500 dark:text-slate-400 tracking-widest text-right pr-4">Điểm số</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 dark:divide-white/5">
                    {MOCK_RANKINGS.map((row, idx) => (
                      <tr key={idx} className="group hover:bg-slate-50 dark:hover:bg-white/5 transition-all">
                        <td className="py-4 pl-4">
                          <p className="text-sm font-light text-slate-950 dark:text-white group-hover:text-primary transition-colors">{row.orgName}</p>
                          <p className="text-[10px] text-slate-600 dark:text-slate-500 font-light mt-0.5">Mã ĐV: {row.orgCode}</p>
                        </td>
                        <td className="py-4">
                          <span className="text-[10px] font-light text-slate-600 dark:text-slate-300 bg-slate-100 dark:bg-white/5 px-2.5 py-1 rounded-lg border border-slate-200 dark:border-white/10">
                            Bộ ngành
                          </span>
                        </td>
                        <td className="py-4 text-center">
                          <RankingBadge ranking={parseRanking(row.ranking)} size="sm" />
                        </td>
                        <td className="py-4 text-right pr-4">
                          <span className="text-xl font-heading font-light text-slate-900 dark:text-white tracking-tighter">{row.totalScore}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
             </div>
          </div>
        </div>

        {/* Right Sidebar Charts */}
        <div className="col-span-12 lg:col-span-4 space-y-8">
          {/* Phân phối 4 xếp hạng */}
          <div className="glass-panel p-6 shadow-sm">
            <h3 className="text-base font-heading text-slate-950 dark:text-white mb-5 flex items-center gap-2">
              <Award size={18} className="text-primary" />
              Phân phối xếp hạng
            </h3>
            <div className="h-[180px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={MOCK_DISTRIBUTION} layout="vertical" barSize={16}>
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="currentColor" className="text-slate-100 dark:text-white/5" />
                  <XAxis type="number" axisLine={false} tickLine={false} tick={{ fill: '#94a3b8', fontSize: 10, fontWeight: 300 }} />
                  <YAxis type="category" dataKey="name" axisLine={false} tickLine={false} tick={{ fill: '#94a3b8', fontSize: 10, fontWeight: 300 }} width={52} />
                  <Tooltip
                    contentStyle={{ backgroundColor: 'var(--bg-card)', border: '1px solid var(--border-glass)', borderRadius: '12px', fontSize: '11px', fontWeight: 300 }}
                  />
                  <Bar dataKey="value" radius={[0, 8, 8, 0]}>
                    {MOCK_DISTRIBUTION.map((d, i) => <Cell key={i} fill={d.color} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              {MOCK_DISTRIBUTION.map(d => (
                <div key={d.name} className="flex items-center gap-1.5 text-[10px] font-light text-slate-500">
                  <span className="w-2 h-2 rounded-full" style={{ backgroundColor: d.color }} />
                  {d.name}: <span className="font-medium text-slate-700 dark:text-white">{d.value}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="glass-panel p-8 shadow-sm">
            <h3 className="text-lg font-heading text-slate-950 dark:text-white mb-8">Radar 8 TC</h3>
            <div className="h-[250px]">
              <ResponsiveContainer width="100%" height="100%">
                <RadarChart data={[
                  { subject: 'TC1 DM', A: 78, fullMark: 100 },
                  { subject: 'TC2 KN', A: 82, fullMark: 100 },
                  { subject: 'TC3 DVC', A: 65, fullMark: 100 },
                  { subject: 'TC4 CL', A: 88, fullMark: 100 },
                  { subject: 'TC5 AN', A: 71, fullMark: 100 },
                  { subject: 'TC6 ND', A: 90, fullMark: 100 },
                  { subject: 'TC7 KT', A: 68, fullMark: 100 },
                  { subject: 'TC8 HT', A: 76, fullMark: 100 },
                ]}>
                  <PolarGrid stroke="currentColor" className="text-slate-200 dark:text-white/5" />
                  <PolarAngleAxis dataKey="subject" tick={{fill: '#64748b', fontSize: 10, fontWeight: 300}} />
                  <Radar name="Toàn quốc" dataKey="A" stroke="var(--primary)" fill="var(--primary)" fillOpacity={0.4} />
                </RadarChart>
              </ResponsiveContainer>
            </div>
            <div className="mt-8 space-y-5">
               {[
                 { label: 'TC1 Danh mục & Công bố', val: 78, color: 'indigo' },
                 { label: 'TC4 Chất lượng dữ liệu',  val: 88, color: 'emerald' },
                 { label: 'TC5 An ninh thông tin',    val: 71, color: 'rose' },
                 { label: 'TC6 Kết nối NDOP',         val: 90, color: 'cyan' },
                 { label: 'TC7 Kiểm toán nội bộ',     val: 68, color: 'violet' },
               ].map(item => (
                 <div key={item.label}>
                     <div className="flex justify-between text-[10px] font-light text-slate-600 dark:text-slate-400 mb-2 tracking-wide">
                       <span>{item.label}</span>
                       <span className="text-slate-950 dark:text-white">{item.val}%</span>
                    </div>
                    <div className="h-1.5 bg-slate-100 dark:bg-white/5 rounded-full overflow-hidden border border-slate-200/50 dark:border-none shadow-inner">
                       <motion.div 
                         initial={{ width: 0 }}
                         animate={{ width: `${item.val}%` }}
                         className={`h-full bg-${item.color}-500 shadow-[0_0_15px_rgba(0,0,0,0.1)]`}
                       />
                    </div>
                 </div>
               ))}
            </div>
          </div>

          <div className="glass-panel p-6 overflow-hidden relative shadow-sm">
            <h3 className="text-lg font-heading text-slate-950 dark:text-white mb-8 flex items-center gap-2">
               <div className="w-2.5 h-2.5 bg-rose-500 rounded-full animate-pulse shadow-[0_0_15px_#f43f5e]" />
               Cảnh báo gần đây
            </h3>
            <div className="space-y-4">
              {[
                { org: 'UBND tỉnh Cà Mau', msg: 'Mất kết nối SOC 24/7', time: '5p', sev: 'Critical' },
                { org: 'Bộ NN&PTNT', msg: 'Bất thường đồng bộ dữ liệu', time: '23p', sev: 'Warning' },
                { org: 'UBND tỉnh Nghệ An', msg: '8 kiến nghị quá hạn', time: '1h', sev: 'Warning' },
              ].map((alert, i) => (
                <div key={i} className="p-5 rounded-3xl bg-slate-50 dark:bg-white/5 border border-slate-200 dark:border-white/10 hover:border-primary/40 transition-all cursor-pointer group shadow-sm">
                   <div className="flex justify-between items-start mb-2">
                      <span className={`text-[10px] font-light px-2.5 py-1 rounded-lg tracking-widest ${
                        alert.sev === 'Critical' ? 'bg-rose-500/10 text-rose-600 dark:text-rose-400' : 'bg-amber-500/10 text-amber-600 dark:text-amber-400'
                      }`}>
                        {alert.sev}
                      </span>
                      <span className="text-[10px] text-slate-400 font-light">{alert.time}</span>
                   </div>
                   <p className="text-xs font-light text-slate-950 dark:text-white group-hover:text-primary transition-colors">{alert.org}</p>
                   <p className="text-[11px] text-slate-600 dark:text-slate-400 mt-1 leading-relaxed font-light">{alert.msg}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
